import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  CalendarDays,
  Search,
  AlertCircle,
  MapPin,
  Clock,
  Video,
  Gavel,
  Users,
  Timer,
  Tag,
  Plus,
  X,
  Send
} from 'lucide-react'
import api from '../config/api'
import { useBranding } from '../hooks/useBranding'
import { formatDate } from '../utils/formatDate'
import '../styles/Appointments.css'

function Appointments() {
  const { branding } = useBranding()
  const firmName = branding?.firmName || 'Your Legal Team'
  const [appointments, setAppointments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [showRequest, setShowRequest] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const [requestForm, setRequestForm] = useState({
    type: 'consultation',
    preferredDate: '',
    preferredTime: '',
    reason: '',
    notes: ''
  })

  useEffect(() => {
    fetchAppointments()
  }, [])

  const fetchAppointments = async () => {
    try {
      setLoading(true)
      const response = await api.get('/appointments')
      setAppointments(response.data.data)
    } catch (err) {
      setError('Failed to load appointments')
    } finally {
      setLoading(false)
    }
  }

  const filteredAppointments = appointments.filter(appt => {
    const matchesSearch = appt.title?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filter === 'all' || appt.type === filter
    return matchesSearch && matchesFilter
  })

  const typeConfig = {
    hearing: { color: 'type-court', icon: Gavel, label: 'Hearing' },
    court_date: { color: 'type-court', icon: Gavel, label: 'Court Date' },
    meeting: { color: 'type-meeting', icon: Users, label: 'Meeting' },
    consultation: { color: 'type-meeting', icon: Users, label: 'Consultation' },
    deadline: { color: 'type-deadline', icon: Timer, label: 'Deadline' },
    deposition: { color: 'type-court', icon: Gavel, label: 'Deposition' },
    mediation: { color: 'type-meeting', icon: Users, label: 'Mediation' },
    other: { color: 'type-other', icon: Tag, label: 'Other' }
  }

  const getTypeConfig = (type) => {
    return typeConfig[type] || { color: 'type-other', icon: Tag, label: type || 'Event' }
  }

  const statusClasses = {
    confirmed: 'status-confirmed',
    pending: 'status-pending',
    cancelled: 'status-cancelled'
  }

  const formatTime = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return ''
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const formatDayNumber = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return ''
    return date.getDate()
  }

  const formatDayName = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return ''
    return date.toLocaleDateString([], { weekday: 'short' }).toUpperCase()
  }

  const formatMonthYear = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return ''
    return date.toLocaleDateString([], { month: 'short', year: 'numeric' })
  }

  const isVirtualLink = (location) => {
    if (!location || typeof location !== 'string') return false
    return location.startsWith('http') || location.toLowerCase().includes('zoom') || location.toLowerCase().includes('meet')
  }

  const uniqueTypes = [...new Set(appointments.map(a => a.type).filter(Boolean))]

  const handleRequestAppointment = async (e) => {
    e.preventDefault()
    if (!requestForm.preferredDate || !requestForm.reason) return
    setRequesting(true)
    try {
      await api.post('/appointments/request', requestForm)
      setShowRequest(false)
      setRequestForm({ type: 'consultation', preferredDate: '', preferredTime: '', reason: '', notes: '' })
      fetchAppointments()
    } catch (err) {
      setError('Failed to submit appointment request')
    } finally {
      setRequesting(false)
    }
  }

  return (
    <div className="appointments-page">
      <header className="page-header">
        <div className="header-content">
          <CalendarDays size={28} />
          <div>
            <h1>Appointments</h1>
            <p>Your upcoming appointments and court dates</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowRequest(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0.5rem 1rem', borderRadius: 8, background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', border: 'none' }}>
          <Plus size={16} /> Request Appointment
        </button>
      </header>

      {showRequest && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '1.5rem', width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#1e293b' }}>Request Appointment with {firmName}</h3>
              <button onClick={() => setShowRequest(false)} style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#64748b' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleRequestAppointment}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: '#475569', fontWeight: 500 }}>Type</label>
                <select value={requestForm.type} onChange={(e) => setRequestForm(p => ({ ...p, type: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #e2e8f0', color: '#1e293b' }}>
                  <option value="consultation">Consultation</option>
                  <option value="meeting">Meeting</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: '#475569', fontWeight: 500 }}>Preferred Date</label>
                  <input type="date" required value={requestForm.preferredDate} onChange={(e) => setRequestForm(p => ({ ...p, preferredDate: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #e2e8f0', color: '#1e293b' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: '#475569', fontWeight: 500 }}>Preferred Time</label>
                  <input type="time" value={requestForm.preferredTime} onChange={(e) => setRequestForm(p => ({ ...p, preferredTime: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #e2e8f0', color: '#1e293b' }} />
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: '#475569', fontWeight: 500 }}>Reason</label>
                <input type="text" required placeholder="What is this appointment for?" value={requestForm.reason} onChange={(e) => setRequestForm(p => ({ ...p, reason: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #e2e8f0', color: '#1e293b' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: '#475569', fontWeight: 500 }}>Additional Notes (optional)</label>
                <textarea rows="3" placeholder="Any details to share with the firm..." value={requestForm.notes} onChange={(e) => setRequestForm(p => ({ ...p, notes: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #e2e8f0', color: '#1e293b', resize: 'vertical' }} />
              </div>
              <button type="submit" disabled={requesting} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0.5rem 1.25rem', borderRadius: 8, background: '#2563eb', color: '#fff', fontWeight: 600, cursor: 'pointer', border: 'none', opacity: requesting ? 0.6 : 1 }}>
                <Send size={16} /> {requesting ? 'Submitting...' : 'Submit Request'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="appointments-controls">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search appointments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-pills">
          <button
            className={`filter-pill ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          {uniqueTypes.map((type) => {
            const config = getTypeConfig(type)
            return (
              <button
                key={type}
                className={`filter-pill ${filter === type ? 'active' : ''}`}
                onClick={() => setFilter(type)}
              >
                {config.label}
              </button>
            )
          })}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading appointments...</p>
        </div>
      ) : filteredAppointments.length === 0 ? (
        <div className="empty-state">
          <CalendarDays size={48} />
          <h3>No appointments found</h3>
          <p>
            {searchTerm
              ? 'Try adjusting your search terms'
              : 'You don\'t have any upcoming appointments'}
          </p>
        </div>
      ) : (
        <div className="appointments-list">
          {filteredAppointments.map((appt, index) => {
            const config = getTypeConfig(appt.type)
            const TypeIcon = config.icon

            return (
              <motion.div
                key={appt.id}
                className={`appointment-card ${config.color}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="appointment-date-block">
                  <span className="date-day-name">{formatDayName(appt.startDateTime)}</span>
                  <span className="date-day-number">{formatDayNumber(appt.startDateTime)}</span>
                  <span className="date-month">{formatMonthYear(appt.startDateTime)}</span>
                </div>

                <div className="appointment-details">
                  <div className="appointment-header">
                    <h3 className="appointment-title">{appt.title}</h3>
                    <span className={`appointment-status ${statusClasses[appt.status] || ''}`}>
                      {appt.status}
                    </span>
                  </div>

                  <div className="appointment-meta">
                    <span className="appointment-type-badge">
                      <TypeIcon size={14} />
                      {config.label}
                    </span>

                    <span className="appointment-time">
                      <Clock size={14} />
                      {formatTime(appt.startDateTime)}
                      {appt.endDateTime && ` - ${formatTime(appt.endDateTime)}`}
                    </span>

                    {appt.location && typeof appt.location === 'string' && (
                      <span className="appointment-location">
                        {isVirtualLink(appt.location) ? (
                          <>
                            <Video size={14} />
                            <a
                              href={appt.location}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Join Virtual Meeting
                            </a>
                          </>
                        ) : (
                          <>
                            <MapPin size={14} />
                            {appt.location}
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default Appointments
